import { html, css, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import type Job from '../../../../../models/job.js';
import definition from '@job-schemas/public-ledger/auto-payments/raise-target.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { raiseTargetFigures, raiseTargetRows } from '../../../../../helpers/auto-payment-cards.js';
import { CHANGE_STARTING, CHANGE_START_FAILED } from '../../../../../helpers/auto-payment-changes.js';
import { autopayCardAction } from '../../../../../helpers/auto-payments-api.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

/**
 * Raise the automatic payments operating target (#25 phase 6): an action waits because it needs more credits than
 * automatic payments top the company's operating account up to (refusal reason ABOVE_OPERATING_TARGET). One open card per
 * company, raised by liquidity-check, which keeps its figures up to date, and claimed by a signatory. Raise starts Change
 * automatic payments amounts as this card's child, for the signatory who pressed it, on the operating target and
 * pre-filled with the suggested one; the card closes when the change applies, and a new one opens when it doesn't. Once
 * the new target applies, liquidity-check tops the operating account up and tells the people waiting. The holder can
 * cancel it (card-action cancel): the people waiting are told the target won't be raised, they keep waiting, and no new
 * card is raised for the same jobs (only a different job's need raises one). It never closes by itself when nothing waits
 * for it any more: only a raise that applies or a cancel ends it.
 * A signed stop (#40) closes it `stopped` (public-ledger/autopay/stop-finish).
 */
@customElement(NSPublicLedgerAutoPaymentsRaiseTarget.is)
export default class NSPublicLedgerAutoPaymentsRaiseTarget extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-raise-target';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-raise-target'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  private async __raise() {
    await this._startChange('raise', CHANGE_STARTING, CHANGE_START_FAILED);
  }

  // Cancel: the card just closes; the server tells each person waiting for the raise that it won't happen, and no new
  // Raise card is raised for their jobs
  private async __cancel() {
    const job = this._job;
    if (!job || this._busy) return;
    this._busy = true;
    this.clearError();
    // Approved (round 4 follow-ups, small string 3)
    this.loading('Cancelling');
    try {
      await autopayCardAction(this._auth?.token, job.id, 'cancel');
      this._closeWhenComplete = true;
      const updated = await this._db?.getById<Job>('job', job.id);
      if (updated) this._jobUpdated(updated);
    } catch (err) {
      this.showError(lambdaRefusal(err, 'This job could not be cancelled. Please try again.'));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  render() {
    const figures = raiseTargetFigures(this._job?.metadata);
    return html`
      <ns-job-layout>
        <p slot="intro">
          A job waiting for nodeStream credits needs more than your company’s operating account is topped up to.
        </p>
        ${this._renderWaiting() ??
        (this._claimed || this._completed
          ? html`
              <div class="job-panel">
                <h3>The operating target is too low</h3>
                <p>
                  Raising the operating target lets automatic payments top the operating account up far enough for it.
                  The signatories sign the new target on the public ledger; once it applies, the people waiting are told
                  they can complete their jobs again.
                </p>
              </div>
            `
          : this._renderClaimNote('raise the operating target'))}
        <div class="job-panel">
          <h3>The figures</h3>
          <ns-detail-list .rows=${raiseTargetRows(figures)}></ns-detail-list>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>Why the target needs raising</h3>
          <p>
            Automatic payments top the operating account up to its target and no further, so an action that needs more
            than that waits until the target is raised. The new target can’t be more than the company target buys; if it
            would be, raise the company target too.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this._completed || this._waiting !== null) return nothing;
    if (!this._claimed) return this._renderClaimButton();
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this._busy}
        @click=${() => this.__cancel()}
      >
        Cancel
      </wa-button>
      <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__raise()}>Raise</wa-button>
    `;
  }
}
